import clsx from 'clsx';
import { forwardRef, type DetailedHTMLProps, type TextareaHTMLAttributes, useMemo } from 'react';

import { Label, calculateInputWidth } from '../shared';

export type TextAreaProps = {
  label?: string;
  value?: string | number;
  fullWidth?: boolean; // New prop to control width behavior
} & DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, className, disabled, value, placeholder, fullWidth = false, rows = 3, ...rest },
  ref,
) {
  // Calculate dynamic width based on placeholder text (only when not fullWidth)
  const dynamicWidth = useMemo(() => {
    if (fullWidth) return 'w-full';
    return calculateInputWidth(placeholder, false);
  }, [placeholder, fullWidth]);

  return (
    <Label text={label} className={fullWidth ? 'w-full' : ''}>
      <textarea
        {...rest}
        value={value}
        ref={ref}
        disabled={disabled}
        placeholder={placeholder}
        rows={rows}
        className={clsx(
          'flex items-start rounded-md border bg-white text-sm resize-none',
          'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
          'outline-none transition-[color,box-shadow]',
          'placeholder:text-muted-foreground',
          'px-3 py-2', // Consistent padding
          {
            'cursor-not-allowed opacity-50': disabled,
          },
          // Apply className first, then width to ensure width takes precedence
          className,
          dynamicWidth, // Dynamic width based on placeholder or full width - applied last
        )}
      />
    </Label>
  );
});
